import React, { Suspense, useCallback, useMemo, useRef } from 'react';
import { Flex } from '@bynder/design-system';
import { lazyPrefetching } from 'packages/helpers/helpers';
import Duration from 'packages/common/Duration';
import { DurationModes } from 'packages/common/Duration/useDuration';
import { useTrimCorrection } from 'packages/common/Duration/useTrimCorrection';
import useTrimShortcuts from 'packages/hooks/shortcuts/useTrimShortcuts';
import useTrimHistory from 'packages/hooks/shortcuts/useTrimHistory';
import { millisecondToFrames } from 'packages/common/editor/editorHelper';
import Loader from 'packages/common/loader/Loader';
import {
    PortionSelectorContainer,
    PortionSelectorPlayer,
    PortionSelectorPlayerWrapper,
} from './PortionSelector.styled';
import { type SimplePlayerHandle } from 'packages/common/player/Simple';
import {
    type FrameNumberType,
    type MillisecondNumberType,
    type PercentNumberType,
} from 'packages/common/Duration/types';

export const SimplePlayer = lazyPrefetching(
    () =>
        import(
            /* webpackChunkName: "videojs" */
            /* webpackPrefetch: true */
            'packages/common/player/Simple'
        ),
);

type PortionSelectorProps = {
    elementId: number;
    elementDuration: number;
    duration: number;
    offsetTime: number;
    resizableRange: boolean;
    frameRate: number;
    handleOnChange: (offsetFrame: number, durationFrame: number | null) => void;
    url: string;
    type: string;
    isEditorPage: boolean;
};

// TODO: Get rid of classNames and their files
const PortionSelector = ({
    elementId,
    elementDuration,
    duration,
    offsetTime,
    resizableRange,
    frameRate,
    handleOnChange,
    url,
    type,
    isEditorPage,
}: PortionSelectorProps) => {
    const playerRef = useRef<SimplePlayerHandle | null>(null);

    const elementDurationInFrames = useMemo(
        () => millisecondToFrames<FrameNumberType>(elementDuration, frameRate),
        [elementDuration, frameRate],
    );
    const offsetInFrames = useMemo(
        () => millisecondToFrames<FrameNumberType>(offsetTime, frameRate),
        [offsetTime, frameRate],
    );
    const durationInFrames = useMemo<number>(
        () => millisecondToFrames<FrameNumberType>(duration, frameRate),
        [duration, frameRate],
    );

    const isFileLongerThanAsset = elementDuration > duration;
    const actionsDisabled = isFileLongerThanAsset && !isEditorPage;
    const isSlipMode = !resizableRange;

    const endInFrames = useMemo(() => {
        const endMs = isFileLongerThanAsset ? offsetTime + duration : offsetTime + elementDuration;

        return millisecondToFrames(endMs, frameRate);
    }, [isFileLongerThanAsset, offsetTime, duration, elementDuration, frameRate]);

    const { correctStartFrame, correctEndFrame } = useTrimCorrection({
        elementId,
        isEditorPage,
        isSlipMode,
        fullDuration: duration,
        elementDuration,
        frameRate,
        start: offsetInFrames,
        end: endInFrames,
        fullDurationInFrames: durationInFrames,
    });

    const { recordChange, undo, redo } = useTrimHistory({
        offsetFrame: offsetInFrames,
        durationFrame: elementDurationInFrames,
    });

    const handleOnChangeWithHistory = useCallback(
        (offsetFrame: number, durationFrame: number | null, forceNewEntry = false) => {
            handleOnChange(offsetFrame, durationFrame);
            recordChange(offsetFrame, durationFrame, forceNewEntry);
        },
        [handleOnChange, recordChange],
    );

    const handleUndo = useCallback(() => {
        const snapshot = undo();

        if (snapshot) {
            handleOnChange(snapshot.offsetFrame, snapshot.durationFrame);
        }
    }, [undo, handleOnChange]);

    const handleRedo = useCallback(() => {
        const snapshot = redo();

        if (snapshot) {
            handleOnChange(snapshot.offsetFrame, snapshot.durationFrame);
        }
    }, [redo, handleOnChange]);

    const changeRange = useCallback(
        (leftPercentage: number, widthPercentage?: number) => {
            const durationInSeconds = duration / 1000;
            const newOffsetTime = Math.round((frameRate * durationInSeconds * leftPercentage) / 100);
            let newElementDuration: number | null = null;

            if (resizableRange && widthPercentage) {
                const maxDuration = durationInSeconds * frameRate - newOffsetTime;
                const calculatedDuration = Math.round((frameRate * durationInSeconds * widthPercentage) / 100);

                newElementDuration = Math.min(calculatedDuration, maxDuration);
            }

            handleOnChangeWithHistory(newOffsetTime, newElementDuration);
        },
        [duration, frameRate, handleOnChangeWithHistory, resizableRange],
    );

    useTrimShortcuts({
        playerRef,
        offsetTime,
        elementDuration,
        fullDuration: duration,
        frameRate,
        isSlipMode,
        onTrimChange: handleOnChangeWithHistory,
        onUndo: handleUndo,
        onRedo: handleRedo,
        correctStartFrame,
        correctEndFrame,
    });

    if (!url) {
        return null;
    }

    // NOTE: This calculation needs to be done in FRAMES!
    // it's necessary because of the floor in the convertation
    const calculatedWidth = (elementDurationInFrames / durationInFrames) * 100;
    const calculatedOffset = (offsetInFrames / durationInFrames) * 100;
    const leftPercentage = calculatedOffset >= 100 ? 0 : calculatedOffset;
    const widthPercentage = isFileLongerThanAsset
        ? 100 - leftPercentage
        : calculatedWidth <= 100
          ? calculatedWidth
          : 100; // this consitions refactor later

    const start = offsetTime;
    const end = isFileLongerThanAsset ? start + duration : start + elementDuration;

    if (!duration) {
        return (
            <Flex alignItems="center" justifyContent="center">
                <Loader size={40} />
            </Flex>
        );
    }

    return (
        <>
            <PortionSelectorPlayer>
                <Suspense fallback={<div>Loading...</div>}>
                    <SimplePlayer
                        ref={playerRef}
                        src={url}
                        type={type}
                        value={{ min: start, max: end }}
                        frameRate={frameRate}
                        controls
                    />
                </Suspense>
            </PortionSelectorPlayer>
            <PortionSelectorContainer>
                <PortionSelectorPlayerWrapper />
                {duration > 0 && (
                    <Duration
                        elementId={elementId}
                        actionsDisabled={actionsDisabled}
                        isResizeDisabled={actionsDisabled}
                        start={offsetInFrames}
                        end={millisecondToFrames(end, frameRate)}
                        duration={
                            isFileLongerThanAsset
                                ? ((durationInFrames - offsetInFrames) as FrameNumberType)
                                : elementDurationInFrames
                        }
                        mode={resizableRange ? DurationModes.TRIM : DurationModes.SLIP}
                        onChange={changeRange}
                        fullDuration={duration as MillisecondNumberType}
                        elementDuration={elementDuration as FrameNumberType}
                        widthPercentageDefault={widthPercentage as PercentNumberType}
                        leftPercentageDefault={leftPercentage as PercentNumberType}
                        frameRate={frameRate as FrameNumberType}
                        isEditorPage={isEditorPage}
                    />
                )}
            </PortionSelectorContainer>
        </>
    );
};

export default PortionSelector;
